import { GtmConfig } from "./models/gtm-config.model";

export interface CognitoConfig {
  userPoolId: string;
  defaultClientId: string;
  /** Microsoft IDP app client. When absent the OAuth code-exchange flow is disabled. */
  idpClientId?: string;
  /** Base domain, e.g. https://xxx.auth.us-east-1.amazoncognito.com */
  domain: string;
}

export enum EnvId {
  Local = 0,
  Dev = 1,
  Test = 2,
  Prod = 3,
}

export interface Env {
  // From .env.json
  id: EnvId;
  apiUrl: string;
  newApiUrl: string;
  version: string;
  gtmConfig?: Partial<GtmConfig>;
  cognito: CognitoConfig;
  testsUrl: string;
  userManualBaseUrl: string;
  // Default values
  defaultDateTimeFormat: string;
  defaultDateFormat: string;
  defaultTimezone: string;
  serverDateTimeFormat: string;
  pageLimits: number[];
}

const defaultSettings = {
  version: process.env.REACT_APP_version as string,
  defaultDateTimeFormat: "M/D/YYYY h:mma",
  defaultDateFormat: "M/D/YYYY",
  defaultTimezone: "America/New_York",
  serverDateTimeFormat: "YYYY-MM-DDTHH:mm:ss.SSS[Z]",
  pageLimits: [24, 48, 96],
};

const cognitoDefaults: CognitoConfig = {
  userPoolId: "us-east-1_6FrJR3rsV",
  defaultClientId: "1plonh9nrb0hrarivp6as29qjf",
  idpClientId: "655gk9npl99b2u8gbu09j08kil",
  domain: "https://zapit-auth-prod.auth.us-east-1.amazoncognito.com",
};

const allEnvSettings = {
  local: {
    PORT: 5000,
    NODE_ENV: "development",
    REACT_APP_id: 0,
    REACT_APP_apiUrl: "https://test-api-5.zapitmedical.com/test/",
    REACT_APP_newApiUrl: "http://localhost:4009/",
    TESTS_URL: "https://tests.dev.zapitqa.com/",
    USER_MANUAL_BASE_URL: "https://user-manual.dev.zapitqa.com",
    cognito: {
      userPoolId: "us-east-1_ONkON38kU",
      defaultClientId: "7sd7ah37saklqfsi2pdblg7v70",
      idpClientId: "",
      domain: "https://zapit-auth-dev.auth.us-east-1.amazoncognito.com",
    },
  },
  dev: {
    NODE_ENV: "development",
    REACT_APP_id: 1,
    REACT_APP_apiUrl: "https://api-v4.dev.zapitqa.com/",
    REACT_APP_newApiUrl: "https://api.dev.zapitqa.com/",
    TESTS_URL: "https://tests.dev.zapitqa.com/",
    USER_MANUAL_BASE_URL: "https://user-manual.dev.zapitqa.com",
    cognito: {
      userPoolId: "us-east-1_ONkON38kU",
      defaultClientId: "7sd7ah37saklqfsi2pdblg7v70",
      idpClientId: "cs65hn662he367uudthd23aur",
      domain: "https://zapit-auth-dev.auth.us-east-1.amazoncognito.com",
    },
  },
  test: {
    NODE_ENV: "production",
    REACT_APP_id: 2,
    REACT_APP_apiUrl: "https://api-v4.test.zapitqa.com/",
    REACT_APP_newApiUrl: "https://api.test.zapitqa.com/",
    TESTS_URL: "https://tests.test.zapitqa.com/",
    USER_MANUAL_BASE_URL: "https://user-manual.test.zapitqa.com",
    REACT_APP_gtmConfig: '{ "id": "GTM-5F7BHSN", "env": { "gtmAuth": "pnwnejRK9RCxtjEXOuWQLw", "gtmPreview": "env-7" } }',
    cognito: {
      userPoolId: "us-east-1_GHZAwjreV",
      defaultClientId: "4se4b2v4513ms6e072pj5jg2nt",
      idpClientId: "5nhojrk1h7379n15fd8tvm68al",
      domain: "https://zapit-auth-test.auth.us-east-1.amazoncognito.com",
    },
  },
  prod: {
    NODE_ENV: "production",
    REACT_APP_id: 3,
    REACT_APP_apiUrl: "https://api-v4.zapitqa.com/",
    REACT_APP_newApiUrl: "https://api.zapitqa.com/",
    REACT_APP_gtmConfig: '{ "id": "GTM-5F7BHSN" }',
    TESTS_URL: "https://tests.zapitqa.com/",
    USER_MANUAL_BASE_URL: "https://user-manual.zapitqa.com",
    cognito: cognitoDefaults,
  },
};

function getEnvId(): EnvId {
  // if building locally (or in docker) check if REACT_APP_env is set.
  const npmEnv = process.env.REACT_APP_env;
  if (npmEnv) {
    console.log(`Using ${npmEnv} environment`);
    return envIdFromString(npmEnv.toLocaleLowerCase());
  }

  // if running as static js, check URL for environment
  const host = window.location.hostname.toLocaleLowerCase();
  if (host === "localhost") return EnvId.Local;

  // figure out env from subdomain (app.z.com is prod, app-test.z.com, app-dev.z.com is dev)
  const subdomain = host.split(".")[0].replace("app", "").replace("-", "") || "prod";
  return envIdFromString(subdomain);
}

function envIdFromString(name: string): EnvId {
  if (name === "prod") return EnvId.Prod;
  else if (name === "test") return EnvId.Test;
  else if (name === "dev") return EnvId.Dev;
  else if (name === "local") return EnvId.Local;
  throw new Error("Unable to set config");
}

function buildEnv(): Env {
  const appId = getEnvId();

  const envSettings = (allEnvSettings as any)[EnvId[appId].toLocaleLowerCase()];
  return {
    ...defaultSettings,
    id: appId,
    apiUrl: envSettings.REACT_APP_apiUrl as string,
    newApiUrl: envSettings.REACT_APP_newApiUrl as string,
    gtmConfig: envSettings.REACT_APP_gtmConfig ? JSON.parse(envSettings.REACT_APP_gtmConfig as string) : undefined,
    cognito: envSettings.cognito,
    testsUrl: envSettings.TESTS_URL,
    userManualBaseUrl: envSettings.USER_MANUAL_BASE_URL as string,
  };
}

const environment: Env = buildEnv();
export default environment;
